import type { RngState } from './rng';

/**
 * Setting-agnostic archetypes. The theme pack (packages/content) maps these to
 * names, sprites and sounds. The simulation never knows what a "zombie" is.
 *
 * Every archetype is a structure on the board: it blocks enemy movement, has HP,
 * and merges with an equal one into the next level.
 *  rapid / splash / burn / chain  shoot
 *  repair                         heals the base
 *  barricade                      only blocks, lots of HP
 *  barracks                       spawns soldiers that walk out and fight in melee
 */
export type ArchetypeId =
  | 'rapid'
  | 'splash'
  | 'burn'
  | 'chain'
  | 'repair'
  | 'barricade'
  | 'barracks'
  | 'archery'
  | 'stable'
  | 'mine';
export const ARCHETYPES: readonly ArchetypeId[] = [
  'rapid',
  'splash',
  'burn',
  'chain',
  'repair',
  'barricade',
  'barracks',
  'archery',
  'stable',
  'mine',
];
/** Spawners: each produces its own kind of soldier (melee, ranged, fast heavy). */
export const SPAWNERS: readonly ArchetypeId[] = ['barracks', 'archery', 'stable'];

/**
 * Merge traits: at balance.traitLevels a building gains a new behaviour on top of the ×2.1, so a merge
 * is something to want, not only a way to free a cell. Two per archetype, in order of level.
 */
export type TraitId =
  | 'twin' // rapid: every shot also hits a second enemy in range
  | 'pierce' // rapid: shots ignore armour
  | 'wide' // splash: blast radius +50 %
  | 'flak' // splash: can hit flyers
  | 'spread' // burn: the flame also sets neighbours of the target on fire
  | 'inferno' // burn: burning damage ×2
  | 'fork' // chain: +2 chain targets
  | 'shock' // chain: chained enemies are slowed for a second
  | 'crew' // repair: also patches the most battered building
  | 'fast_heal' // repair: heals twice as often
  | 'spikes' // barricade: attackers take damage with every blow
  | 'regen' // barricade: regrows 2 % HP a second during the fight
  | 'squad' // barracks: +1 soldier alive
  | 'veterans' // barracks: soldiers +50 % HP
  | 'longbow' // archery: riflemen +0.7 cell range
  | 'volley' // archery: +1 rifleman alive
  | 'pair' // stable: +1 rider alive
  | 'charge' // stable: riders +50 % damage
  | 'vault' // mine: income +50 %
  | 'interest'; // mine: after every wave +5 % of your gold (max 25)
export const TRAIT_IDS: readonly TraitId[] = [
  'twin', 'pierce', 'wide', 'flak', 'spread', 'inferno', 'fork', 'shock', 'crew', 'fast_heal',
  'spikes', 'regen', 'squad', 'veterans', 'longbow', 'volley', 'pair', 'charge', 'vault', 'interest',
];

/** Hero gear: one item per slot, each slot feeds one hero stat. */
export type GearSlot = 'weapon' | 'armor' | 'boots' | 'charm';
export const GEAR_SLOTS: readonly GearSlot[] = ['weapon', 'armor', 'boots', 'charm'];
export type GearRarity = 'common' | 'rare' | 'epic';
export const GEAR_RARITIES: readonly GearRarity[] = ['common', 'rare', 'epic'];
export interface GearItem {
  id: number;
  slot: GearSlot;
  rarity: GearRarity;
  /** Percent bonus to the slot's stat. */
  bonus: number;
}
/** Percent multipliers the equipped gear applies to the hero; 100 = nothing equipped. */
export interface HeroGearMods {
  hpPct: number;
  dmgPct: number;
  speedPct: number;
  aoePct: number;
  /** Set bonus: change to the hits between shockwaves (negative = more often). */
  aoeEveryDelta: number;
}

export type EnemyArchId = 'walker' | 'runner' | 'brute' | 'boss' | 'flyer' | 'armored';
export const ENEMY_ARCHETYPES: readonly EnemyArchId[] = ['walker', 'runner', 'brute', 'boss', 'flyer', 'armored'];

export type PerkId =
  | 'rapid_dmg'
  | 'splash_radius'
  | 'burn_duration'
  | 'chain_targets'
  | 'range_all'
  | 'wall_max'
  | 'wave_gold'
  | 'cheap_units'
  | 'open_free'
  | 'repair_boost'
  | 'barricade_hp'
  | 'barracks_fast'
  | 'army_cap';
export const PERKS: readonly PerkId[] = [
  'rapid_dmg',
  'splash_radius',
  'burn_duration',
  'chain_targets',
  'range_all',
  'wall_max',
  'wave_gold',
  'cheap_units',
  'open_free',
  'repair_boost',
  'barricade_hp',
  'barracks_fast',
  'army_cap',
];

/** Skill tree nodes: permanent percent bonuses bought with medals. Effects live in skills.ts. */
export type SkillId =
  | 'turret_dmg'
  | 'turret_range'
  | 'structure_hp'
  | 'wall_hp'
  | 'soldier_power'
  | 'start_gold'
  | 'wave_gold'
  | 'mine_income'
  | 'cheap_shop'
  | 'hero_power'
  | 'hero_speed';
export const SKILL_IDS: readonly SkillId[] = [
  'turret_dmg',
  'turret_range',
  'structure_hp',
  'wall_hp',
  'soldier_power',
  'start_gold',
  'wave_gold',
  'mine_income',
  'cheap_shop',
  'hero_power',
  'hero_speed',
];
export type SkillBranch = 'fire' | 'defense' | 'economy' | 'hero';
export interface SkillNode {
  id: SkillId;
  branch: SkillBranch;
  maxLevel: number;
  /** Node in the same branch that must have at least one level first. */
  requires?: SkillId;
}
/** What the bought skills do to a run, as integer percents and flat adds; 100 / 0 = nothing bought. */
export interface SkillMods {
  turretDmgPct: number;
  rangeAdd: number;
  structureHpPct: number;
  wallHpAdd: number;
  soldierPct: number;
  startGoldAdd: number;
  waveGoldPct: number;
  minePct: number;
  /** Percent taken off every building price. */
  priceDiscountPct: number;
  heroPct: number;
  heroSpeedPct: number;
}

/** Run modifiers for challenges. Applied in createRun and BattleSim; part of the state digest. */
export type ModifierId =
  | 'no_hero'
  | 'fast_enemies'
  | 'tough_enemies'
  | 'only_shooters'
  | 'only_soldiers'
  | 'poor'
  | 'glass_bunker'
  | 'air_raid'
  | 'iron_horde';
export const MODIFIER_IDS: readonly ModifierId[] = [
  'no_hero',
  'fast_enemies',
  'tough_enemies',
  'only_shooters',
  'only_soldiers',
  'poor',
  'glass_bunker',
  'air_raid',
  'iron_horde',
];
/** A weekly challenge: a map played under modifiers for a one-time medal reward. */
export interface ChallengeTemplate {
  id: string;
  mapId: string;
  modifiers: ModifierId[];
  medals: number;
  /** Waves to clear to complete it; default = every authored wave (a win). */
  goalWaves?: number;
}

export interface Cell {
  x: number;
  y: number;
}

export interface Unit {
  id: number;
  arch: ArchetypeId;
  level: number; // 1..balance.maxLevel
}

/**
 * closed = obstacle (tree, rubble): blocks enemies AND building; clearing it for gold opens
 * a build slot but also opens a path. That trade-off is the tactical layer.
 * cost <= 0 means the obstacle is permanent and can never be cleared.
 */
export type CellState =
  | { kind: 'closed'; cost: number }
  | { kind: 'empty' }
  | { kind: 'unit'; unit: Unit }
  /** Part of the base building. Impassable, unbuildable; enemies attack it from adjacent cells. */
  | { kind: 'base' };

export type Phase = 'placing' | 'battle' | 'perk' | 'won' | 'lost';

/** Everything the player can do. This log + seed + mapId is the whole run; the server replays it. */
export type Action =
  | { t: 'buy'; slot: number; to: Cell }
  | { t: 'move'; from: Cell; to: Cell }
  | { t: 'open'; cell: Cell }
  | { t: 'sell'; cell: Cell }
  /** Buy a shop slot straight into a pocket slot: keep it for later when the board is full or the spot is not ready. */
  | { t: 'stash'; slot: number; pocket: number }
  /** Pocket → board (an equal building on the target merges, as a shop drop does). */
  | { t: 'place'; pocket: number; to: Cell }
  /** Board → pocket: lift a building off the field to rearrange or to keep it for the next wave. */
  | { t: 'pocket'; from: Cell; pocket: number }
  | { t: 'sell_pocket'; pocket: number }
  /** Pocket → pocket: move into an empty slot, merge onto an equal building, swap with a different one. */
  | { t: 'pocket_move'; from: number; to: number }
  | { t: 'repair' }
  | { t: 'reroll' }
  | { t: 'fight' }
  | { t: 'perk'; index: number };

export interface RunState {
  mapId: string;
  seed: number;
  rng: RngState;
  phase: Phase;
  /** 1-based index of the wave to fight next (or being fought). */
  wave: number;
  /** Authored wave count; on an endless map the run never reaches 'won' and this only labels the UI. */
  totalWaves: number;
  endless: boolean;
  gold: number;
  /** Base HP (the thing enemies want to reach at the bottom edge). */
  wallHp: number;
  wallMax: number;
  width: number;
  height: number;
  /** Row-major, index = y * width + x. Row 0 is the enemy spawn edge, row height-1 touches the base. */
  grid: CellState[];
  /** Building is allowed only in the bottom `buildRows` rows; grows after each cleared wave. */
  buildRows: number;
  buildRowsPerWave: number;
  shop: ArchetypeId[];
  /** Temporary storage, balance.pocketSlots long. Pocketed buildings do not fight and do not earn. */
  pocket: (Unit | null)[];
  purchases: number;
  rerollsThisWave: number;
  wallBuys: number;
  perks: PerkId[];
  perkOffer: PerkId[];
  nextUnitId: number;
  kills: number;
  /** Merges made over the run; daily quests read it. */
  merges: number;
  /** Structures destroyed by enemies over the run. */
  lostStructures: number;
  /** Merging grants XP; a full bar earns a perk draft (like the reference game). */
  xp: number;
  xpLevel: number;
  pendingPerks: number;
  /** Hero level from the player profile at run start; 0 = no hero on the field. */
  heroLevel: number;
  heroId: HeroId;
  /** Card level per archetype from the player profile at run start; raises the building's base stats. */
  cardLevels: Record<ArchetypeId, number>;
  /** What the hero's equipped gear adds, fixed at run start. */
  heroGear: HeroGearMods;
  /** Skill tree bonuses from the profile, fixed at run start. */
  skills: SkillMods;
  /** Challenge modifiers this run plays under; empty for a normal run. */
  modifiers: ModifierId[];
  battleTicks: number;
  actions: Action[];
}

/** What a boss does besides being thick; see BattleSim. */
export type BossAbility = 'summon' | 'smash' | 'regen';
export const BOSS_ABILITIES: readonly BossAbility[] = ['summon', 'smash', 'regen'];

export interface SpawnGroup {
  arch: EnemyArchId;
  count: number;
  startTick: number;
  spacing: number;
  /** Percent of base HP, 100 = base. */
  hpPct: number;
  /** Bosses only: their trick for this wave. */
  ability?: BossAbility;
}

/** One chapter of the level ladder: board shape, rocks, enemy roster and difficulty curve for its levels. */
export interface ChapterDef {
  id: string;
  levels: number;
  width: number;
  height: number;
  /**
   * Percent of the build zone covered by rocks at the start. Space is the run's scarce resource:
   * every clearing costs gold that could have been a building, and opens a road for the enemy.
   */
  zoneRocksPct: number;
  /** Percent of zone rocks that can never be cleared (cost 0); they shape the maze for good. */
  zonePermanentPct: number;
  /** Permanent rocks in the enemy rows above the zone: min and max. */
  topRocks: [number, number];
  /** Clearing cost range for breakable rocks, in steps of 5. */
  rockCost: [number, number];
  roster: EnemyArchId[];
  /** hpPct of wave 1 of the chapter's first level. */
  hpStart: number;
  /** Per-wave compounding, 120 = +20 % a wave. */
  hpGrowthPct: number;
  /** Extra percent of hpStart per level inside the chapter. */
  hpPerLevelPct: number;
  wavesMin: number;
  wavesMax: number;
  startGold: number;
  wallHp: number;
  /** Authored map that closes the chapter as its last level. */
  finale?: string;
  medalsFirstWin: number;
  bossAbilities?: BossAbility[];
  doubleBoss?: boolean;
}

export interface WaveDef {
  spawns: SpawnGroup[];
}

export interface MapDef {
  id: string;
  width: number;
  height: number;
  closed: { x: number; y: number; cost: number }[];
  /** The base building's footprint. Without it the base is the bottom edge of the board. */
  base?: { x: number; y: number; w: number; h: number };
  /** How many rows, counted from the bottom, allow building at the start. Default: all. */
  buildRows?: number;
  /** Rows added to the build zone after every cleared wave (never past row 1). Default 1. */
  buildRowsPerWave?: number;
  waves: WaveDef[];
  startGold?: number;
  wallHp?: number;
  /** Set on the survival map: after the authored waves, `waveFor` keeps generating harder ones forever. */
  endless?: EndlessDef;
  /** Map id that must be won before this one unlocks. Absent = open from the start. */
  requires?: string;
}

export interface EndlessDef {
  /** Percent growth of enemy HP per generated wave, compounding in integer math (112 = +12%). */
  hpGrowthPct: number;
  /** hpPct of the first generated wave. */
  startHpPct: number;
}

export interface SoldierStats {
  hp: number;
  dmg: number;
  /** Ticks between hits. */
  interval: number;
  /** Milli-cells per tick. */
  speed: number;
  /** Melee reach, milli-cells. */
  range: number;
}

export interface UnitStats {
  /** Structure HP at level 1. Enemies break through when no free path exists. */
  hp: number;
  /** Damage per shot at level 1. 0 = does not shoot. */
  dmg: number;
  /** Ticks between shots. 0 = does not shoot. */
  cooldown: number;
  /** Milli-cells. */
  range: number;
  splashRadius?: number;
  burnDmg?: number;
  burnTicks?: number;
  chainTargets?: number;
  chainRadius?: number;
  /** Base HP restored per healInterval at level 1. */
  heal?: number;
  healInterval?: number;
  /** Spawners: ticks between soldiers and how many may be alive at once at level 1. */
  spawnInterval?: number;
  spawnMax?: number;
  soldier?: SoldierStats;
  /** Mine: gold added after each cleared wave at level 1. */
  income?: number;
  /** Cannot target flying enemies (the mortar). */
  groundOnly?: boolean;
}

export type HeroId = 'commander' | 'medic' | 'sniper';
export const HERO_IDS: readonly HeroId[] = ['commander', 'medic', 'sniper'];

/** What a hero does on every N-th hit. */
export type HeroSkill =
  | { kind: 'shockwave'; every: number; radius: number }
  | { kind: 'mend'; every: number; wallHeal: number; structureHeal: number }
  /** The N-th shot deals pct% of normal damage (300 = triple) and ignores armour. */
  | { kind: 'snipe'; every: number; pct: number };

export interface HeroStats {
  hp: number;
  dmg: number;
  /** Ticks between hits. */
  interval: number;
  /** Milli-cells per tick. */
  speed: number;
  /** Melee reach. */
  range: number;
  skill: HeroSkill;
  /** Hero level (shared across heroes) at which this hero becomes selectable. */
  unlockLevel: number;
}

export interface EnemyStats {
  hp: number;
  /** Milli-cells per tick. */
  speed: number;
  /** Damage to the base, structures and soldiers, applied every attackInterval ticks. */
  attackDmg: number;
  attackInterval: number;
  bounty: number;
  /** Flies over rocks, buildings and soldiers straight to the base; only shooters and the hero can hit it. */
  flying?: boolean;
  /** Flat damage taken off every hit (never below 1). Burning ignores it. */
  armor?: number;
}

export interface Balance {
  tickMs: number;
  /** Percent multiplier per level, index 0 = level 1. Precomputed, never Math.pow at runtime. */
  levelMult: number[];
  maxLevel: number;
  units: Record<ArchetypeId, UnitStats>;
  unitWeights: Record<ArchetypeId, number>;
  enemies: Record<EnemyArchId, EnemyStats>;
  /** Enemy melee reach against soldiers, milli-cells. */
  enemyMeleeRange: number;
  /** Soldiers (hero excluded) alive at once across all spawners; the "N max" of the reference. Perks add to it. */
  soldierCap: number;
  /** Growth curve of the n-th unit purchase (index = purchases so far) for a base price of 10. Last value repeats. */
  unitPrice: number[];
  /** Base price per archetype on the same scale (10 = the curve as is): a wall is cheap, a garage is not. */
  unitBasePrice: Record<ArchetypeId, number>;
  /** Pocket slots: buildings bought or lifted off the board and kept for later. */
  pocketSlots: number;
  /** Merge levels at which a building gains its first and second trait. */
  traitLevels: [number, number];
  traits: Record<ArchetypeId, [TraitId, TraitId]>;
  wallRepair: { hp: number; prices: number[] };
  rerollBase: number;
  rerollStep: number;
  waveGoldBase: number;
  waveGoldPerWave: number;
  sellRefundPct: number;
  startGold: number;
  wallHp: number;
  maxBattleTicks: number;
  shopSize: number;
  /** XP needed for each successive perk draft; last value repeats. */
  xpCosts: number[];
  /** XP granted by a merge = xpMergeBase * resulting level. */
  xpMergeBase: number;
  /** XP granted for clearing a wave, so non-mergers still see perks now and then. */
  xpWave: number;
  heroes: Record<HeroId, HeroStats>;
  /** Percent multiplier on a building's base stats per card level, index 0 = level 1. Length = max level. */
  cardMult: number[];
  /** Copies needed to raise a card from level i+1 to level i+2. */
  cardCopies: number[];
  /** Percent multiplier on the hero's HP and damage per hero level, index 0 = level 1. Length = max level. */
  heroMult: number[];
  /** Hero XP needed to go from level i+1 to level i+2. */
  heroXpCosts: number[];
  /** What a finished run pays into the profile. */
  rewards: { copiesPerWave: number; winBonusCopies: number; heroXpPerWave: number; winBonusHeroXp: number; medalsPerWave: number; winBonusMedals: number };
  /** Skill tree: medal cost of each successive node level (last value repeats) and the nodes themselves. */
  skills: { costs: number[]; nodes: SkillNode[] };
  /** Login streak: medals paid on the first accepted run of each consecutive day (index = day - 1, last repeats). */
  streak: { medals: number[] };
  /** Weekly challenges: how many are active at once and the pool they are drawn from. */
  challenges: { perWeek: number; pool: ChallengeTemplate[] };
  /** Invite a friend: both get medals once the friend clears goalWaves in one run; a referrer is paid for at most maxInvites friends. */
  referral: { goalWaves: number; medalsEach: number; maxInvites: number };
  /** The level ladder. */
  ladder: { chapters: ChapterDef[] };
  gear: {
    /** Inclusive percent range a fresh item rolls in, per slot and rarity. */
    bonus: Record<GearSlot, Record<GearRarity, [number, number]>>;
    rarityWeights: Record<GearRarity, number>;
    winRarityWeights: Record<GearRarity, number>;
    /** A rewarded run drops one item once this many waves are cleared. */
    dropMinWaves: number;
    inventoryCap: number;
    scrapXp: Record<GearRarity, number>;
    /** Identical unequipped items that merge into one of the next rarity. */
    mergeCount: number;
    /** Set bonuses: two or more rare-or-better items add pairPct to every hero stat; four epics fire the shockwave sooner. */
    sets: { pairMinRarity: GearRarity; pairPct: number; fullEpicAoeEveryDelta: number };
  };
  /** Everything sold for Telegram Stars. One currency layer on purpose: Stars buy the thing itself. */
  shop: {
    /** Approximate retail price of one Star in USD, for the "≈ $" label next to every price. */
    starsUsd: number;
    chests: ChestDef[];
    /** Every N-th chest (any tier) carries an epic item. */
    pityEvery: number;
    /** Chest handed out free once per UTC day. */
    freeChest: ChestId;
    copiesPack: { stars: number; copies: number };
    heroXpPack: { stars: number; xp: number };
    /** One per account, discounted, no randomness: copies of every card, hero XP, medals and one fixed item. */
    starterPack: { stars: number; fullStars: number; copiesEach: number; heroXp: number; medals: number; gear: { slot: GearSlot; rarity: GearRarity; bonus: number } };
    /** Cosmetic field skins, bought once, no effect on play. */
    skins: SkinDef[];
  };
  /** Daily quests: how many per day and the pool they are drawn from. */
  quests: { perDay: number; pool: QuestTemplate[] };
}

export type QuestKind = 'waves' | 'merges' | 'kills' | 'runs' | 'free_chest' | 'win_map';

/** A quest template; the day picks a few and fills in the map for win_map. */
export interface QuestTemplate {
  kind: QuestKind;
  target: number;
  copies: number;
  heroXp: number;
}

export type ChestId = 'crate' | 'container' | 'vault';
export const CHEST_IDS: readonly ChestId[] = ['crate', 'container', 'vault'];
/** Field skins: a palette for the board. 'default' is free and always owned. */
export type SkinId = 'default' | 'night' | 'winter' | 'desert';
export const SKIN_IDS: readonly SkinId[] = ['default', 'night', 'winter', 'desert'];
export interface SkinDef {
  id: SkinId;
  /** 0 = free. */
  stars: number;
}

export type ProductId = ChestId | 'copies' | 'hero_xp' | 'starter' | 'skin';

export interface ChestDef {
  id: ChestId;
  stars: number;
  /** Card copies inside. */
  copies: number;
  /** true = every building equally likely; false = shop weights, common buildings more often. */
  uniform: boolean;
  /** Percent odds of the one gear item's rarity. Shown to the player before buying. */
  gearOdds: Record<GearRarity, number>;
  heroXp: number;
  /** Skill-tree medals inside. */
  medals: number;
}

export class SimError extends Error {
  constructor(
    public readonly code: string,
    message?: string,
  ) {
    super(message ? `${code}: ${message}` : code);
    this.name = 'SimError';
  }
}
